/**
 * Concierge — shared utilities & micro-components.
 *
 * Tiny pieces re-used across the advisor and client Concierge pages so we
 * keep visual language consistent (date dividers, message bubbles, attachment
 * chips, etc.) without ballooning the main page files.
 */
import React from "react";
import { Paperclip, FileText, ImageIcon, Link as LinkIcon, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function resolveAttachmentUrl(att) {
  if (!att?.url) return "";
  if (/^https?:/i.test(att.url)) return att.url;
  return `${API}${att.url}`;
}

export function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
  if (dayStart.getTime() === today.getTime()) return "Today";
  if (dayStart.getTime() === yesterday.getTime()) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: dayStart.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
}

export function AttachmentChip({ att }) {
  const url = resolveAttachmentUrl(att);
  const isImage = (att?.content_type || "").startsWith("image/");
  const Icon = isImage ? ImageIcon : att?.kind === "url" ? LinkIcon : FileText;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[rgba(0,0,0,0.08)] bg-white/70 backdrop-blur-sm hover:border-[hsl(var(--accent-gold))] transition-colors max-w-[260px]"
      data-testid="attachment-chip"
      title={att?.name || url}
    >
      <Icon className="size-3.5 text-[hsl(var(--foreground-muted))] shrink-0" strokeWidth={1.5} />
      <span className="text-[12px] text-[hsl(var(--foreground))] truncate">
        {att?.name || url}
      </span>
      <ExternalLink className="size-3 text-[hsl(var(--foreground-muted))] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={1.5} />
    </a>
  );
}

export function AttachmentGrid({ attachments }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="attachment-grid">
      {attachments.map((a, i) => <AttachmentChip key={i} att={a} />)}
    </div>
  );
}
